"use client";

import React from "react";
import { Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function InitiateYourChinaOperation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}
function ChinaOperationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="w-16 h-16 bg-[#B5A642]/20 rounded-none flex items-center justify-center mb-2">
          <div className="w-8 h-8 text-[#B5A642] rounded-none border-2 border-current flex items-center justify-center">
            <Check className="w-5 h-5" />
          </div>
        </div>
        <h3 className="text-2xl font-serif font-bold text-[#F5F5DC] uppercase tracking-wider">Audit Request Logged</h3>
        <p className="text-[#F5F5DC]/80 font-mono max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[#B5A642] text-[#B5A642] hover:bg-[#B5A642] hover:text-[#001F36] rounded-none transition-colors font-mono uppercase tracking-widest">
          RE-INITIATE SYNC
        </Button>
      </div>;
  }
  const inputClasses = "bg-[#001F36] border-[#B5A642]/20 text-[#F5F5DC] placeholder:text-[#F5F5DC]/20 font-mono focus-visible:ring-[#B5A642] focus-visible:border-[#B5A642] focus-visible:ring-offset-0 transition-all duration-300 hover:border-[#B5A642]/40 h-12 rounded-none caret-[#B5A642]";
  const labelClasses = "text-xs uppercase tracking-widest text-[#B5A642] font-mono font-bold mb-2";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company_name" rules={{
        required: "Legal Entity Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Legal Entity Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter legal entity name" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Primary Decision Maker is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Primary Decision Maker</FormLabel>
              <FormControl>
                <Input placeholder="Name of decision maker" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Secure Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid transmission address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Secure Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ops@agency.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Direct Line is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Direct Line</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="estimated_volume" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Total Service Calls Processed Monthly</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 500" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />

        <FormField control={control} name="start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Current Operational Stack</FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g., ServiceTitan, Salesforce, etc." {...field} className={cn(inputClasses, "block w-full")} />
              </FormControl>
              <FormMessage className="font-mono text-red-500" />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Operational Constraints</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full border px-3 py-2 text-base shadow-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm rounded-none", "bg-[#001F36] border-[#B5A642]/20 text-[#F5F5DC] placeholder:text-[#F5F5DC]/20 font-mono", "focus:border-[#B5A642] focus:ring-1 focus:ring-[#B5A642]", "transition-all duration-300 hover:border-[#B5A642]/40 resize-y caret-[#B5A642]")} placeholder="List any specific pipeline leaks or legacy hurdles..." {...field} />
            </FormControl>
            <FormMessage className="font-mono text-red-500" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-950/40 border border-red-500/50 rounded-none">
          <p className="text-red-400 text-xs font-mono text-center uppercase tracking-widest">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-[#B5A642] hover:bg-[#968935] text-[#001F36] font-black text-lg uppercase tracking-widest shadow-lg shadow-black/40 transition-all duration-300 hover:scale-[1.01] rounded-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            SYNCING PIPELINE...
          </> : 'INITIATE SYSTEM SYNC'}
      </Button>
    </Form>;
}